import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  SvgImage,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  ImageDiv,
  CopyImageButton,
  CopyHTMLButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { FaRegCopy } from 'react-icons/fa6'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
export function SteelPinnedBasePlates() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fy, setFy] = useState(250)
  const [fu, setFu] = useState(400)
  const [fck, setFck] = useState(20)
  const [bf, setBf] = useState('')
  const [tf, setTf] = useState('')
  const [d, setD] = useState('')
  const [a2, setA2] = useState('')
  const [ncsk, setNcsk] = useState('')
  const [hvsk, setHvsk] = useState(0)
  const [gammaf, setGammaf] = useState(1.4)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState('W150X13,0')
  const [profiles, setProfiles] = useState([])
  const [hasSelectedProfile, setHasSelectedProfile] = useState(true)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'steel-pinned-baseplate-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    async function fetchProfiles() {
      const response = await api.get('/wprofiles')
      setProfiles([...response.data.profiles, { name: 'Personalizado' }])
      const profile = response.data.profiles[0]
      setBf(Number((profile.bf / 10).toFixed(2)))
      setTf(Number((profile.tf / 10).toFixed(2)))
      setD(Number((profile.d / 10).toFixed(2)))
    }

    fetchProfiles()
  }, [])

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFy(entry.fy)
      setFu(entry.fu)
      setFck(entry.fck)
      setBf(entry.bf)
      setTf(entry.tf)
      setD(entry.d)
      setA2(entry.a2)
      setNcsk(entry.ncsk)
      setHvsk(entry.hvsk)
      setGammaf(entry.gammaf || 1.4)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'hvsk' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fy = validateField('fy', fy)
    newErrors.fu = validateField('fu', fu)
    newErrors.fck = validateField('fck', fck)
    newErrors.bf = validateField('bf', bf)
    newErrors.tf = validateField('tf', tf)
    newErrors.d = validateField('d', d)
    newErrors.a2 = validateField('a2', a2)
    newErrors.ncsk = validateField('ncsk', ncsk)
    newErrors.hvsk = validateField('hvsk', hvsk)
    newErrors.gammaf = validateField('gammaf', gammaf)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fy, fu, fck, bf, tf, d, a2, ncsk, hvsk, gammaf, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  const handleProfileChange = (event) => {
    const selectedProfile = event.target.value

    if (selectedProfile === 'Personalizado') {
      setHasSelectedProfile(false)
      setBf('')
      setTf('')
      setD('')
    } else {
      const profile = profiles.find((profile) => profile.name === selectedProfile)
      setBf(Number((profile.bf / 10).toFixed(2)))
      setTf(Number((profile.tf / 10).toFixed(2)))
      setD(Number((profile.d / 10).toFixed(2)))
    }

    setSelectedProfile(selectedProfile)
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const ncsd = ncsk * gammaf
      const hvsd = hvsk * gammaf

      const data = {
        fy,
        fu,
        fck,
        bf,
        tf,
        d,
        a2,
        ncsd,
        hvsd,
      }

      setData(data)

      const response = await api.post('/steel/pinned-baseplate', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fy,
        fu,
        fck,
        bf,
        tf,
        d,
        a2,
        ncsk,
        hvsk,
        gammaf,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fy,
      fu,
      fck,
      bf,
      tf,
      d,
      a2,
      ncsk,
      hvsk,
      gammaf,
      result,
      data,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFy(jsonData.fy)
        setFu(jsonData.fu)
        setFck(jsonData.fck)
        setBf(jsonData.bf)
        setTf(jsonData.tf)
        setD(jsonData.d)
        setA2(jsonData.a2)
        setNcsk(jsonData.ncsk || jsonData.ncsd || '')
        setHvsk(jsonData.hvsk || jsonData.hvsd || 0)
        setGammaf(jsonData.gammaf || 1.4)
        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const inputImageRef = useRef(null)

  const InputImage = () => {
    return (
      <SvgImage ref={inputImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />
        <rect x="69" y="120.5" width="362" height="259" stroke="black" strokeWidth="2" />
        <path
          d="M112 154L117.406 151.972L112.946 148.305L112 154ZM114.858 150.524L115.245 150.842L171.797 82.0767L171.411 81.7592L171.025 81.4416L114.472 150.207L114.858 150.524Z"
          fill="black"
        />
        <line x1="222.715" y1="81.6794" x2="170.715" y2="81.6794" stroke="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="170.715" y="78.1914">
            {a2 != 0 && a2 != '' ? a2 : 'A2'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="244.289" y="355.273">
            {d != 0 && d != '' ? d : 'd'}
          </tspan>
        </text>
        <line x1="160.5" y1="322" x2="160.5" y2="369" stroke="black" />
        <line x1="340.5" y1="322" x2="340.5" y2="369" stroke="black" />
        <path
          d="M161 360.5L166 363.387V357.613L161 360.5ZM340 360.5L335 357.613V363.387L340 360.5ZM165.5 360.5V361H335.5V360.5V360H165.5V360.5Z"
          fill="black"
        />
        <text
          transform="translate(374.686 249.5) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="0.185546" y="19.2727">
            {bf != 0 && bf != '' ? bf : 'bf'}
          </tspan>
        </text>
        <line x1="350.686" y1="314" x2="405.686" y2="314" stroke="black" />
        <line x1="350.686" y1="185" x2="405.686" y2="185" stroke="black" />
        <path
          d="M398.686 313.5L401.573 308.5L395.8 308.5L398.686 313.5ZM398.686 185.5L395.8 190.5L401.573 190.5L398.686 185.5ZM398.686 309L399.186 309L399.186 190L398.686 190L398.186 190L398.186 309L398.686 309Z"
          fill="black"
        />
        <rect x="137" y="169.5" width="226" height="161" stroke="black" strokeWidth="2" />
        <rect x="160.5" y="185.5" width="4" height="127" stroke="black" strokeWidth="2" />
        <rect x="335.5" y="185.5" width="4" height="127" stroke="black" strokeWidth="2" />
        <rect
          x="334.5"
          y="247.5"
          width="3.00001"
          height="169"
          transform="rotate(90 334.5 247.5)"
          stroke="black"
          strokeWidth="2"
        />
        <circle cx="250" cy="215.22" r="4.5" stroke="black" />
        <line x1="260" y1="215.22" x2="240" y2="215.22" stroke="black" />
        <line x1="250" y1="225.72" x2="250" y2="205.72" stroke="black" />
        <circle cx="250" cy="283.78" r="4.5" stroke="black" />
        <line x1="260" y1="283.78" x2="240" y2="283.78" stroke="black" />
        <line x1="250" y1="294.28" x2="250" y2="274.28" stroke="black" />
      </SvgImage>
    )
  }

  const handleCopySVG = async (svgRef) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 500
      canvas.height = 500
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Bases rotuladas</span>
          </div>
          <h1>⚙️ Bases rotuladas de aço</h1>
          <p className="subtitle">
            Dimensionamento de placas de base com ligação rotulada considerando resistência da placa e chumbadores
          </p>
          <div className="badges">
            <Badge>📘 ABNT NBR 8800:2008</Badge>
            <Badge>📏 Placas de base</Badge>
            <Badge>🧰 Chumbadores</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Calcula dimensões mínimas da placa de base, espessura necessária e bitola/comprimento de chumbadores para
              ligações rotuladas em pilares de aço.
            </p>
            <p>
              Usa parâmetros de material do perfil e concreto, esforços atuantes e permite selecionar perfis
              pré-cadastrados ou inserir valores personalizados.
            </p>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            <InputImage />
            <CopyImageButton
              type="button"
              title="Clique para copiar essa imagem"
              onClick={() => handleCopySVG(inputImageRef)}
            >
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência do Metal do Perfil (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy || ''}
                onChange={(event) => setFy(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência do Metal do Chumbador (MPa)</span>
              <ValidatedInput
                name="fu"
                type="number"
                value={fu || ''}
                onChange={(event) => setFu(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            {profiles.length > 0 && (
              <InputDiv>
                <span>Perfil</span>
                <div>
                  <Select
                    style={{ marginLeft: '0' }}
                    id="profileSelect"
                    value={selectedProfile}
                    onChange={handleProfileChange}
                  >
                    {profiles.map((profile, index) => (
                      <option key={index} value={profile.name}>
                        {profile.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </InputDiv>
            )}
            <InputDiv>
              <span>Largura da flange (bf, cm)</span>
              <ValidatedInput
                name="bf"
                type="number"
                value={bf || ''}
                onChange={(event) => setBf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading || hasSelectedProfile}
              />
            </InputDiv>
            <InputDiv>
              <span>Espessura da flange (tf, cm)</span>
              <ValidatedInput
                name="tf"
                type="number"
                value={tf || ''}
                onChange={(event) => setTf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading || hasSelectedProfile}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura do perfil (d, cm)</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d || ''}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading || hasSelectedProfile}
              />
            </InputDiv>
            <InputDiv>
              <span>Área da Base de Concreto (cm²)</span>
              <ValidatedInput
                name="a2"
                type="number"
                value={a2 || ''}
                onChange={(event) => setA2(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Esforço Vertical Característico (kN)</span>
              <ValidatedInput
                name="ncsk"
                type="number"
                value={ncsk || ''}
                onChange={(event) => setNcsk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Esforço Horizontal Característico (kN)</span>
              <ValidatedInput
                name="hvsk"
                type="number"
                value={hvsk}
                onChange={(event) => setHvsk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Placa de base</h3>
              <MathDiv>
                <h4>Dimensões em planta</h4>
                <BlockMath math={`H = ${result.dimensions.h.toFixed(2) * 10} \\, mm`} />
                <BlockMath math={`B = ${result.dimensions.b.toFixed(2) * 10} \\, mm`} />
                <h4>Espessura</h4>
                {result.plate ? (
                  <BlockMath math={`  t_n = ${result.plate.toFixed(2)} \\, mm`} />
                ) : (
                  <BlockMath math={`  \\text{Nenhuma espessura de chapa disponível} `} />
                )}
              </MathDiv>
              <h3>Chumbadores</h3>
              <MathDiv>
                <h4>Bitola</h4>
                <p>
                  Serão necessários dois chumbadores de {result.bolt.name}, resultando uma área de{' '}
                  {result.boltsArea.toFixed(2)} cm².
                </p>
                <h4>Comprimento</h4>
                <BlockMath
                  math={`L_{cs} = 12 \\cdot \\phi = 12 \\cdot ${result.bolt.d.toFixed(2)} = ${result.lcs.toFixed(2)} \\, mm`}
                />
                <p>
                  Deve-se adicionar a esse valor a espessura de regularização da base, a espessura da plata de base e o
                  comprimento de pega para instalação de arruelas, porcas e contraporcas
                </p>
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Placa de base</h3>
              <MathDiv>
                <h4>Área da placa</h4>
                <BlockMath>{String.raw`A_1 \geq \left\{ \begin{array}{l} a \\ b \\ c \end{array} \right.`}</BlockMath>
                <BlockMath
                  math={`a = \\left( \\dfrac{1}{A_2} \\right) \\cdot \\left( \\dfrac{N_{c,sd}}{0,5 \\cdot f_{ck}} \\right)^2`}
                />
                <BlockMath
                  math={`a = \\left( \\dfrac{1}{${data.a2}} \\right) \\cdot \\left( \\dfrac{${data.ncsd}}{0,5 \\cdot ${data.fck / 10}} \\right)^2`}
                />
                <BlockMath math={`a = ${result.baseplateArea.first.toFixed(1)} \\, mm^2`} />
                <BlockMath
                  math={`b = \\dfrac{N_{c,sd}}{f_{ck}}=\\dfrac{${data.ncsd}}{${data.fck / 10}} = ${result.baseplateArea.second.toFixed(1)} \\, cm^2`}
                />
                <BlockMath
                  math={`c = d \\cdot b_f = ${data.d} \\cdot ${data.bf} = ${result.baseplateArea.third.toFixed(1)} \\, cm^2`}
                />
                <BlockMath math={`\\boxed{A_1 = ${result.baseplateArea.a1.toFixed(1)} \\, cm^2}`} />
                <h4>Dimensões em planta</h4>
                <BlockMath math={`H = {A_1}^{1/2} + 0,5 \\cdot \\left( 0.95 \\cdot d - 0,8 \\cdot b_f \\right)`} />
                <BlockMath math={`H = {${result.baseplateArea.a1.toFixed(1)}}^{1/2} + \\ldots`} />
                <BlockMath
                  math={`\\ldots + 0,5 \\cdot \\left( 0.95 \\cdot ${data.d} - 0,8 \\cdot ${data.bf} \\right)`}
                />
                <BlockMath math={`H = ${result.dimensions.calculatedH.toFixed(2)} \\, cm`} />
                <BlockMath
                  math={`B = \\dfrac{A_1}{H} = \\dfrac{${result.baseplateArea.a1.toFixed(1)}}{${result.dimensions.calculatedH.toFixed(2)}} = ${result.dimensions.calculatedB.toFixed(2)} \\, cm`}
                />
                <BlockMath math={`\\boxed{H = ${result.dimensions.h.toFixed(2) * 10} \\, mm}`} />
                <BlockMath math={`\\boxed{B = ${result.dimensions.b.toFixed(2) * 10} \\, mm}`} />
                <h4>Espessura</h4>
                <BlockMath>{String.raw`A_H \geq \left\{ \begin{array}{l} a \\ b \end{array} \right.`}</BlockMath>
                <BlockMath
                  math={`a = \\dfrac{N_{c,sd}}{0,5 \\cdot f_{ck} \\cdot {\\dfrac{A_2}{b_f \\cdot d}}^{1/2}}`}
                />
                <BlockMath
                  math={`a = \\dfrac{${data.ncsd}}{0,5 \\cdot ${data.fck / 10} \\cdot {\\dfrac{${data.a2} }{${data.bf} \\cdot ${data.d}}^{1/2}}}`}
                />
                <BlockMath math={`a = ${result.ah.first.toFixed(1)} \\, cm^2`} />
                <BlockMath
                  math={`b = \\dfrac{N_{c,sd}}{f_{ck}} = \\dfrac{${data.ncsd}}{${data.fck / 10}} = ${result.ah.second.toFixed(1)} \\, cm^2`}
                />
                <BlockMath math={` \\boxed{A_H =${result.ah.ah.toFixed(1)} \\, cm^2}`} />
                <BlockMath
                  math={`m = \\dfrac{H - 0,95 \\cdot d}{2} = \\dfrac{${result.dimensions.h.toFixed(2) * 10} - 0,95 \\cdot ${data.d * 10}}{2}`}
                />
                <BlockMath math={`m = ${(result.tickness.cantilevers.m * 10).toFixed(1)} \\, mm`} />
                <BlockMath
                  math={`n = \\dfrac{B - 0,8 \\cdot b_f}{2} = \\dfrac{${result.dimensions.b.toFixed(2) * 10} - 0,8 \\cdot ${data.bf * 10}}{2}`}
                />
                <BlockMath math={`n = ${(result.tickness.cantilevers.n * 10).toFixed(1)} \\, mm`} />
                <BlockMath
                  math={`c = 0,25 \\cdot \\left[ \\alpha - \\left( \\alpha^2 - 4 \\cdot \\left( A_H - b_f \\cdot t_f \\right) \\right) \\right]`}
                />
                <BlockMath math={`\\text{onde } \\alpha = d+b_f-t_f`} />
                <BlockMath
                  math={`\\alpha^2 - 4 \\cdot \\left( A_H - b_f \\cdot t_f \\right) ${
                    result.tickness.cantilevers.condition.condition ? `< 0 \\therefore c \\notin \\mathbb{R}` : `>= 0`
                  }`}
                />
                {!result.tickness.cantilevers.condition.condition && (
                  <BlockMath math={`c = ${result.tickness.cantilevers.c.toFixed(2)} \\, mm`} />
                )}
                <BlockMath>{String.raw`t_n \geq \left\{ \begin{array}{l} a \\ b \\ c' \end{array} \right.`}</BlockMath>
                <BlockMath
                  math={`a = m \\cdot \\left( \\dfrac{2,2 \\cdot N_{c,sd}}{B \\cdot H \\cdot f_y} \\right)^{1/2}`}
                />
                <BlockMath math={`a = ${result.tickness.tn1.toFixed(2)} \\, mm`} />
                <BlockMath
                  math={`b = n \\cdot \\left( \\dfrac{2,2 \\cdot N_{c,sd}}{B \\cdot H \\cdot f_y} \\right)^{1/2}`}
                />
                <BlockMath math={`b = ${result.tickness.tn2.toFixed(2)} \\, mm`} />
                <BlockMath
                  math={`c' = c \\cdot \\left( \\dfrac{2,2 \\cdot N_{c,sd}}{B \\cdot H \\cdot f_y} \\right)^{1/2}`}
                />
                {Number.isNaN(result.tickness.tn3) && (
                  <BlockMath math={`c = ${result.tickness.tn3.toFixed(2)} \\, mm`} />
                )}
                <BlockMath math={` t_n \\geq ${result.tickness.tn.toFixed(1)} \\, mm`} />
                {result.plate ? (
                  <BlockMath math={` \\boxed{ t_n = ${result.plate.toFixed(2)} \\, mm}`} />
                ) : (
                  <BlockMath math={` \\boxed{ \\text{Nenhuma espessura de chapa disponível} }`} />
                )}
              </MathDiv>
              <h3>Chumbadores</h3>
              <MathDiv>
                <h4>Bitola</h4>
                <BlockMath
                  math={`A_{cs} = \\dfrac{1,35 \\cdot H_d}{0,4 \\cdot f_u} = \\dfrac{1,35 \\cdot ${data.hvsd}}{0,4 \\cdot ${data.fu / 10}}`}
                />
                <BlockMath math={`A_{cs} = ${result.acs.toFixed(2)} \\, cm^2`} />
                <p>
                  Serão necessários dois chumbadores de {result.bolt.name}, resultando uma área de{' '}
                  {result.boltsArea.toFixed(2)} cm².
                </p>
                <h4>Comprimento</h4>
                <BlockMath
                  math={`L_{cs} = 12 \\cdot \\phi = 12 \\cdot ${result.bolt.d.toFixed(2)} = ${result.lcs.toFixed(2)} \\, mm`}
                />
                <p>
                  Deve-se adicionar a esse valor a espessura de regularização da base, a espessura da plata de base e o
                  comprimento de pega para instalação de arruelas, porcas e contraporcas
                </p>
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
